import { StrictMode, lazy } from 'react';
import SuspenseWrapper from '@/components/SuspenseWrapper.tsx';
import DeferredBoot from '@/components/DeferredBoot.tsx';
import { createRoot } from 'react-dom/client';
import {
  createRouter,
  createRootRoute,
  createRoute,
  RouterProvider,
  HeadContent,
  Navigate,
} from '@tanstack/react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import AppRoot from '@/app';
import ANCILARY_PAGES from '@/routes/ancillary.routes';
import JOURNEY_PAGES_V9 from '@/routes/journey.routes/v9';
import COMPACT_PAGES from '@/routes/compact.routes';
import { GENERIC_REDIRECT_WHITELIST_PATHS } from '@/routes/constants';
import MainLayout from '@/layouts/MainLayout';
import CompactLayout from '@/layouts/CompactLayout';
import GetStartedLayout from '@/layouts/GetStartedLayout';
const GenericRedirect = lazy(() => import('@/pages/GenericRedirect'));
import { getPageTitleFormatted } from './utils/functions';

import { preloadJourneyChunks } from '@/routes/journey.routes/v9';

const LazyAnalyticsInitializer = lazy(() => import('@/components/AnalyticsInitializer'));
const LazyUserVisitTracker = lazy(() => import('@/components/UserVisitTracker'));

const queryClient = new QueryClient();

const rootRoute = createRootRoute({
  component: () => {
    return (
      <>
        <HeadContent />
        <AppRoot />
      </>
    );
  },
});

const genericRedirectRoutes =
  (GENERIC_REDIRECT_WHITELIST_PATHS || [])?.map((path) => ({
    path,
    component: () => (
      <CompactLayout>
        <SuspenseWrapper fallbackClassName="h-screen w-full">
          <GenericRedirect />
        </SuspenseWrapper>
      </CompactLayout>
    ),
    title: getPageTitleFormatted('Redirecting...'),
  })) || [];

const JOURNEY_ROUTES: {
  path: string;
  component: any;
  title?: string;
}[] = [
  ...Object.keys(JOURNEY_PAGES_V9).map((key) => ({
    path: JOURNEY_PAGES_V9[key as keyof typeof JOURNEY_PAGES_V9]?.path,
    component: () => (
      <GetStartedLayout>
        {JOURNEY_PAGES_V9[key as keyof typeof JOURNEY_PAGES_V9].component}
      </GetStartedLayout>
    ),
    title: JOURNEY_PAGES_V9[key as keyof typeof JOURNEY_PAGES_V9]?.title,
  })),
];

export const APP_ROUTES: {
  path: string;
  component: any;
  title?: string;
}[] = [
  // --- ANCILARY ---
  ...Object.keys(ANCILARY_PAGES).map((key) => ({
    path: ANCILARY_PAGES[key]?.path,
    component: () => <MainLayout>{ANCILARY_PAGES[key].component}</MainLayout>,
    title: ANCILARY_PAGES[key]?.title,
  })),
  // --- JOURNEY ---
  ...JOURNEY_ROUTES,

  // --- COMPACT ---
  ...Object.keys(COMPACT_PAGES).map((key) => ({
    path: COMPACT_PAGES[key]?.path,
    component: () => <CompactLayout>{COMPACT_PAGES[key].component}</CompactLayout>,
    title: COMPACT_PAGES[key]?.title,
  })),

  // --- GENERIC REDIRECTS ---
  ...genericRedirectRoutes,
];
const routeTree = rootRoute.addChildren(
  APP_ROUTES.map((route) =>
    createRoute({
      getParentRoute: () => rootRoute,
      path: route?.path,
      component: route.component,
      head: () => ({
        meta: [
          {
            title: getPageTitleFormatted(route?.title),
          },
        ],
      }),
    })
  )
);

export const router = createRouter({
  routeTree,
  defaultNotFoundComponent: () => <Navigate to="/" />,
  scrollRestoration: true,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

const startApp = () => {
  const container = document.getElementById('root');
  if (!container) return;

  const root = createRoot(container);

  // GENTLE HYDRATION:
  // We wait for two animation frames to ensure the browser has
  // finished painting the static shell before React starts
  // replacing it. This eliminates 'unattributed' reflows.
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      root.render(
        <StrictMode>
          <QueryClientProvider client={queryClient}>
            <DeferredBoot>
              <SuspenseWrapper fallbackClassName="h-0 w-0 opacity-0">
                <LazyUserVisitTracker />
              </SuspenseWrapper>
              <SuspenseWrapper fallbackClassName="h-0 w-0 opacity-0">
                <LazyAnalyticsInitializer />
              </SuspenseWrapper>
            </DeferredBoot>
            <RouterProvider router={router} />
          </QueryClientProvider>
        </StrictMode>
      );
      setTimeout(preloadJourneyChunks, 0);
    });
  });
};

startApp();
